import * as $ from 'jquery';

import * as rxui from 'common/rxui/core';
import { Observable, Subject, BehaviorSubject } from 'common/barrels/rx';
import { serialDisposable, disposableSubscription, disposable, Lazy } from 'common/barrels/core';
import { Color } from 'common/color';
import { div } from 'common/dom/primitives';

import { Control } from 'runtime/controls/control';
import { ControlArgs } from 'runtime/controls/controlargs';
import { FilteredLookupItemsRequested } from 'runtime/events/filteredlookupitemsrequested';
import { LookupItemByDisplayRequested } from 'runtime/events/lookupitembydisplayrequested';
import { LookupItemRequested } from 'runtime/events/lookupitemrequested';
import { LookupItem } from 'runtime/data/lookupitem';
import { registerBinding } from 'runtime/bindingengine';

import 'runtime/styles/controls/hierarchy.less';

class LookupResult {
    constructor(public filter: string, public items: LookupItem[]) { }
}

const navKeys = { 40: 1, 38: -1 };
const maxListHeight = 300;
const listMargin = 20;
const defaultPopulationDelay = 250;

class HierarchyLookupItem extends LookupItem {
    public FullDisplayValue: string;
    public AddedIndex: number;
}

export class Hierarchy extends Control {
    private value = new BehaviorSubject<LookupItem>(null);
    private setValues = new Subject<string>();
    private listDisposable = serialDisposable(null);
    private $input: JQuery;
    private $icon: JQuery;
    private listOpen = false;
    private readyTrigger = new Subject<any>();
    private initialized = false;
    private updating = false;
    private focusGroup: BehaviorSubject<JQuery>;
    private tempItemsList: LookupItem[] = [];

    constructor(args: ControlArgs) {
        super(args);

        this.methods.register(this.config.elementName, this);

        this.$element.addClass('control-container hierarchy');
        this.$input = $('<input />')
            .css('text-align', this.config['content.TextAlignment'].toLowerCase())
            .conditionalCss(this.config['content.FontFamily'], 'font-family')
            .conditionalCss(this.config['content.FontStyle'], 'font-style')
            .css('font-size', this.config['content.FontSize'] + 'px')
            .css('font-weight', this.config['content.FontWeight'].toLowerCase())
            .conditionalCss(this.config['content.Foreground'], 'color',() => Color.splitArgb(this.config['content.Foreground'])[0])
            .applyBackground(this.config['content.Background'])
            .appendTo(this.$element);

        this.focusGroup = new BehaviorSubject<JQuery>(this.$input);

        this.$icon = $('<i />').addClass('fa fa-long-arrow-down').appendTo(this.$element);

        const valueSet = this.setValues.withLatestFrom(this.value, (x, y) => ({newKey: x, currentItem: y})).share();

        const checkValueTrigger = this.focusGroup.switchMap(x => rxui.groupBlur(x)).map(() => this.$input.val());

        const input = rxui.from(this.$input, 'propertychange input').filter(() => !this.updating).share();

        this.applyTooltip(this.$input.add(this.$icon));

        const nullOrEmpty = x => x === null || x === '';

        const textValue = Observable.of<boolean>(true).merge(Observable.fromEvent(this.$input, 'change keyup paste').map(() => true)).merge(this.value.map(() => true)).map(() => this.$input.val()).distinctUntilChanged().pairwise().share();

        const focusOut = Observable.fromEvent(this.$input, 'focusout');
        const focusIn = Observable.fromEvent(this.$input, 'focusin');

        let items: Lazy<Observable<LookupResult>> = new Lazy(() => Observable.never<LookupResult>());

        const typingTrigger = Observable.fromEvent(this.$input, 'focusin')
            .switchMap(() => input
                .map(() => this.$input.val())
                .distinctUntilChanged()
                .map(() => true)
                .debounceTime(this.config['content.MinimumPopulateDelay'] || defaultPopulationDelay)
                .merge(rxui.filteredKeys(this.$input, [$.ui.keyCode.DOWN]).filter(() => !this.listOpen).map(() => true))
                .filter(() => this.$input.val().length >= this.config['content.MinimumPrefixLength'])
                .takeUntil(Observable.fromEvent(this.$input, 'focusout'))
            );

        const clickTrigger = rxui.mousedownleft(this.$icon)
            .do(e => e.preventDefault())
            .do(() => this.$input.focus())
            .filter(() => this.listOpen === false && this.derivedEnabledState() !== false)
            .filter(() => this.$input.val().length >= this.config['content.MinimumPrefixLength']);

        const [first, second] = typingTrigger.merge(clickTrigger).prioritize();

        this.subscriptions = disposableSubscription(
            first.map(() => this.$input.val())
                .distinctUntilChanged()
                .subscribe(x => items = new Lazy(() => this.getLookupItems(x).publishReplay().refCount())),

            second.switchMap(() =>
                Observable.using(
                    () => ({ unsubscribe: () => this.removeLoading() }),
                    () => {
                        this.showLoading();
                        this.closeList();
                        return this.getLookupItems(this.$input.val()).takeUntil(focusOut);
                    }
                )
            )
                .delay(0)
                .subscribe(x => this.renderList(x, x.filter !== "")),

            input.map(() => this.$input.val()).filter(x => x.length < this.config['content.MinimumPrefixLength'] && this.listOpen).subscribe(() => this.closeList()),
            textValue.filter(x => nullOrEmpty(x[0]) && !nullOrEmpty(x[1])).subscribe(() => this.triggerEvent('ValueEntered')),
            textValue.filter(x => !nullOrEmpty(x[0]) && nullOrEmpty(x[1])).subscribe(() => this.triggerEvent('ValueCleared')),
            checkValueTrigger
                .filter(x => x === '')
                .withLatestFrom(this.value, (_, x) => x)
                .filter(x => x !== null)
                .subscribe(() => this.select(null, true)),

            checkValueTrigger
                .filter(x => x !== '')
                // this is to ensure this runs after the menu handler, which may save us a server roundtrip
                .flatMap(x => Observable.interval(0).first().map(() => x))
                .withLatestFrom(this.value, (x, y) => ({ newDisplay: x, currentItem: y }))
                .filter(x => x.currentItem === null || x.newDisplay !== x.currentItem.displayValue)
                .switchMap(x => this.isCaseInsensitiveMatch(x.newDisplay, x.currentItem) ? Observable.of(x.currentItem) : this.getLookupItemByDisplay(x.newDisplay).takeUntil(focusIn))
                .withLatestFrom(this.value, (x, y) => ({ newValue: x, oldValue: y }))
                .subscribe(x => this.select(x.newValue, !this.itemsEquivalent(x.oldValue, x.newValue))),

            valueSet.filter(x => x.newKey === null).subscribe(() => this.select(null, false)),
            valueSet.filter(x => x.newKey !== null && (x.currentItem === null || x.newKey !== x.currentItem.keyValue))
                .switchMap(x => this.getLookupItemByKey(x.newKey))
                .subscribe(x => this.select(x, false)),

            this.value.distinctUntilChanged().subscribe(x => this.$element.triggerHandler('content.SelectedValue', x === null ? [null] : [x.keyValue])),
            this.listDisposable,
            this.processBindings(this.config),
            this.managePrimaryBinding('content.SelectedValue'),
            this.readyTrigger.filter(() => this.initialized && this.isReady()).first().subscribe(() => this.triggerEvent('Ready for Input')),
            this.readyTrigger,
            Observable.interval(0).first().subscribe(() => this.readyTrigger.next(null))
        );

        this.initialized = true;

        this.initialize();
    }

    dispose() {
        super.dispose();
        this.listDisposable.dispose();
    }

    private isCaseInsensitiveMatch(newDisplayValue: string, currentItem: LookupItem) {
        return currentItem !== null && newDisplayValue !== null && newDisplayValue.toLowerCase() === currentItem.displayValue.toLowerCase();
    }

    private itemsEquivalent(a: LookupItem, b: LookupItem) {
        return a === null && b === null ? true
             : a === null ? false
             : b === null ? false
             : a.keyValue === b.keyValue;
    }

    private makeFilteredList(items: HierarchyLookupItem[], current: HierarchyLookupItem, parent: HierarchyLookupItem, filter: string, ref: {addedIndex: number}) : JQuery[] {
        const childItems = items.filter(item => item.parentKeyValue == current.keyValue);

        let listItems : JQuery[] = [];

        if(current.active && current.displayValue.toLowerCase().indexOf(filter.toLowerCase()) >= 0) {
            current.AddedIndex = ref.addedIndex++;
            listItems.push(this.makeFilteredDisplayTextItem(current, parent));
        } else {
            const index : number = items.indexOf(current, 0);
            if (index > -1) {
                items.splice(index, 1);
            }
            this.makeFilteredDisplayTextItem(current, parent);
        }

        if(childItems.length > 0) {
            childItems.forEach(child => listItems = listItems.concat(this.makeFilteredList(items, child, current, filter, ref)));
        }
        return listItems;
    }

    private makeFilteredDisplayTextItem(item: HierarchyLookupItem, parent: HierarchyLookupItem) : JQuery {
        const text = parent === null ? item.displayValue.replace(" (No Approvers)", '')
            : parent.FullDisplayValue + " > " + item.displayValue.replace(" (No Approvers)", '');

        const displayText = parent === null ? item.displayValue
            : parent.FullDisplayValue + " > " + item.displayValue;

        item.FullDisplayValue = text;

        return $('<li />').append($('<span />').text(displayText).data('lookupitem', item));
    }

    private makeSearchFilteredList(items: LookupItem[], filter: string) : [JQuery, HierarchyLookupItem[]] {
        const newItems = items.map(x => <HierarchyLookupItem>x);

        const i = $('<ul />').addClass('searchList');

        newItems.filter(item => item.parentKeyValue == '')
            .forEach(x => {
                const forRoot = this.makeFilteredList(newItems, x, null, filter, { addedIndex: 0 });
                forRoot.forEach(y => i.append(y));});

        return [i,newItems];
    }

    private makeItems(items: HierarchyLookupItem[], current: HierarchyLookupItem, hierarchyRef: {addedIndex: number}) : JQuery {
        const childItems = items.filter(item => item.parentKeyValue == current.keyValue);
        // make level1 and level2 items available initially
        const parent = items.filter(item => item.keyValue == current.parentKeyValue)[0];
        const available = current.parentKeyValue == '' || (parent && parent.parentKeyValue == '');

        current.AddedIndex = hierarchyRef.addedIndex++;
        if(childItems.length <= 0) {
            return this.makeChildItem(current, available);
        } else {
            const item = this.makeParentItem(current, available);
            const childList = current.parentKeyValue === '' ? $('<ul />').addClass('hierarchy-child hierarchy-active') : $('<ul />').addClass('hierarchy-child');
            childItems.forEach(child =>{
                childList.append(this.makeItems(items, child, hierarchyRef));
            });
            item.append(childList);
            return item;
        }
    }

    private makeChildItem(item: LookupItem, available: boolean) : JQuery {
        const listItem = $("<li />").text(item.displayValue);
        return listItem.wrapInner($('<span />').addClass(available ? 'available': 'unavailable').conditionalClass(!item.active, 'disabled').data('lookupitem', item));
    }

    private makeParentItem(item: LookupItem, available: boolean) : JQuery {
        const iconClass = item.parentKeyValue === '' ? 'fa fa-caret-down hierarchy-caret' : 'fa fa-caret-right hierarchy-caret';
        const selectionClass = available ? 'available' : 'unavailable';
        const listIcon = $("<i />").addClass(iconClass);

        const listItem = $("<li />").append(listIcon);
        return listItem.append($('<span />').addClass(selectionClass).conditionalClass(!item.active, 'disabled').text(item.displayValue).data('lookupitem', item));
    }

    private makeHierarchyList(items: LookupItem[]) : [JQuery, HierarchyLookupItem[]] {
        const newItems = items.map(x => <HierarchyLookupItem>x);

        return [newItems.filter(item => item.parentKeyValue == '')
            .reduce((a, x) => a.append(this.makeItems(newItems, x, { addedIndex: 0 })), $('<ul />')), newItems];

    }

    private filterHierarchyItems(items: LookupItem[], document: Document) {

        const spanTags = document.getElementsByClassName('available') as HTMLCollectionOf<HTMLElement>;
        const filteredItems: LookupItem[] = [];
        for (let i = 0; i < spanTags.length; i++){

            filteredItems.push($(spanTags[i]).data('lookupitem'));
            //filteredItems.push(items.filter(item => item.displayValue == spanTags[i].innerText)[0]);
        }

        return filteredItems//$(spanTags[0]).data('lookupitem');
    }

    private checkActiveChildren(caret) {
        const activeChildren = caret.parentElement.querySelector('ul.hierarchy-active');
        if(activeChildren !== null){
            activeChildren.childNodes.forEach(child => {

                const listElement = child.querySelector('ul');
                if($(listElement).hasClass('hierarchy-active')){
                    child.querySelector('ul.hierarchy-active').childNodes.forEach(li => $(li.querySelector('span.unavailable')).toggleClass('available unavailable'));
                    this.checkActiveChildren(child.querySelector('i'));
                }
            });

        }else{
            caret.parentElement.querySelector('ul').childNodes.forEach(li => $(li.querySelector('span.unavailable')).toggleClass('available unavailable'));
        }
    }

    private showLoading() {
        div('hierarchy-loading')
            .appendTo(this.$element);
    }

    private removeLoading() {
        this.$element.children('.hierarchy-loading').remove();
    }

    private renderList(result: LookupResult, typeEvent: boolean) {
        const items = result.items;

        if (items.length === 0) {
            this.closeList();
            return;
        }

        let $list : JQuery;
        let tempfilteredItems: HierarchyLookupItem[] | LookupItem[];
        let filteredItems: LookupItem[];
        [$list, tempfilteredItems]= typeEvent ? this.makeSearchFilteredList(items, result.filter) : this.makeHierarchyList(items);

        filteredItems = tempfilteredItems.sort((a, b) => {
            const a1 = <HierarchyLookupItem>a;
            const b1 = <HierarchyLookupItem>b;
            return a1.AddedIndex > b1.AddedIndex ? 1 : -1;
        });

        const offset = this.$element.offset();
        const availableAbove = offset.top - listMargin;
        const availableBelow = $(window).height() - offset.top - this.$element.height() - listMargin;
        const direction = availableBelow > maxListHeight ? 'down'
                        : availableAbove > availableBelow ? 'up'
                        : 'down';

        const maxHeight = Math.min(direction === 'up' ? availableAbove : availableBelow, maxListHeight);

        const $popupInner = $("<div />").append($('<div />').append($list)).css({'max-height': maxHeight + 'px', 'max-width': 590 + 'px'});

        const $popup = $("<div tabindex='-1'/>").append($popupInner).addClass('hierarchy-list').css('outline', 'none')
            .css('min-width', this.$element.width())
            .css('text-align', this.config['content.TextAlignment'].toLowerCase())
            .conditionalCss(this.config['content.FontFamily'], 'font-family')
            .conditionalCss(this.config['content.FontStyle'], 'font-style')
            .css('font-size', this.config['content.FontSize'] + 'px')
            .css('font-weight', this.config['content.FontWeight'].toLowerCase())
            .conditionalCss(this.config['content.Foreground'], 'color',() => Color.splitArgb(this.config['content.Foreground'])[0])
            .applyBackground(this.config['content.Background'])
            .appendTo(this.$element.closest('.runtime-application-container'))
            .position({
                my: 'left ' + (direction === 'down' ? 'top' : 'bottom'),
                at: 'left ' + (direction === 'down' ? 'bottom' : 'top'),
                of: this.$element,
                collision: 'flipfit none'
            });

        this.focusGroup.next(this.$input.add($list).add($popup).add($popupInner));

        this.listOpen = true;

        const selection = new BehaviorSubject<LookupItem>(null);

        const selectionClass = 'hierarchy-selected';

        this.tempItemsList = this.filterHierarchyItems(filteredItems, document);//Initializing the temporary list with the default filtered items

        let focusedArea = typeEvent? this.$input : $popup;

        this.listDisposable.set(disposableSubscription(
            disposable(() => $popup.remove()),

            rxui.clickOutside($list.add(this.$element)).subscribe(() => this.closeList()),

            rxui.groupBlur(this.$input.add($list).add($popup).add($popupInner))
                .withLatestFrom(selection.skip(1),(x, y) => y)
                .withLatestFrom(this.value, (x, y) => ({ newValue: x, oldValue: y }))
                .subscribe(x => {
                        if (this.itemsEquivalent(x.oldValue, x.newValue)) {
                            this.closeList();
                            return;
                        }

                        if (x !== null) {
                            this.select(x.newValue, true);
                        }
                        else {
                            const selectedValue = items.filter(y => (y.displayValue === result.filter) || (y.displayValue.toLowerCase() === result.filter.toLowerCase()))[0] || null;
                            this.select(selectedValue, true);
                        }
                    }
                ),

            rxui.groupBlur(this.$input.add($list).add($popup).add($popupInner)).subscribe(() => this.closeList()),

            rxui.filteredKeys(this.$input, [$.ui.keyCode.ESCAPE]).subscribe(e => {
                this.setText(result.filter);
                this.closeList();
                e.stopPropagation();
            }),

            rxui.key(focusedArea).map(x => navKeys[x]).filter(x => x !== undefined)
                .withLatestFrom(selection.asObservable(), (x, y) => ({ nav: x, current: y }))
                .subscribe(x => {
                    const itemsList = typeEvent ? filteredItems : this.tempItemsList;

                    const currentIndex = x.current === null ? 0 : itemsList.indexOf(x.current) + 1;
                    const continuum = [itemsList[itemsList.length - 1], null, ...itemsList, null];
                    const newSelection = continuum[currentIndex + 1 + x.nav];
                    selection.next(newSelection);
                }),

            selection.pairwise().subscribe(x => {
                const tag = typeEvent ? 'span' : 'span.available';
                if (x[0] !== null)
                    $list.find(tag).toArray().map(y => $(y)).filter(y => y.data('lookupitem') === x[0])[0].removeClass(selectionClass);

                if (x[1] != null) {
                    const $item = $list.find(tag).toArray().map(y => $(y)).filter(y => y.data('lookupitem') === x[1])[0];
                    $item.addClass(selectionClass);

                    this.scrollItemIntoView($item, $popup);
                }

                this.setText(x[1] !== null && x[1].active ? x[1].displayValue : result.filter);
            }),

            rxui.filteredKeys($popup, [$.ui.keyCode.RIGHT])
                .map(x => $(x.target))
                .subscribe(function(x){

                    const selectedItem = x.find('.hierarchy-selected')[0];
                    const caret = selectedItem && selectedItem.parentElement.querySelector('i');

                    if($(caret).hasClass('fa-caret-down')){
                        const press = $.Event('keydown');
                        press.keyCode = $.ui.keyCode.DOWN;
                        $popup.trigger(press);
                    }else{
                        caret && caret.click();
                    }
                }),

            rxui.filteredKeys($popup, [$.ui.keyCode.LEFT])
                .map(x => $(x.target))
                .subscribe(function(x){

                    const selectedItem = x.find('.hierarchy-selected')[0];
                    const caret = selectedItem && selectedItem.parentElement.querySelector('i');

                    if($(caret).hasClass('fa-caret-down')){
                        caret && caret.click();
                    }else{
                        if(selectedItem !== undefined){
                            const parentElement = $(selectedItem).parents().eq(2).find('span')[0];
                            const parentLookupItem = <LookupItem>($(parentElement).data('lookupitem'));
                            $(parentElement).addClass(selectionClass);
                            this.scrollItemIntoView($(parentElement), $popup);
                            this.setText(parentLookupItem.displayValue);
                            selection.next(parentLookupItem);
                        }
                    }
                }.bind(this)),

            rxui.filteredKeys(this.$input, [$.ui.keyCode.DOWN])
                .filter(() => !typeEvent)
                .filter(() => this.listOpen)
                .subscribe(function(){
                    $popup.focus();
                    const press = $.Event("keydown");
                    press.keyCode = $.ui.keyCode.DOWN;
                    $popup.trigger(press);
                }),

            rxui.click($popup.find('i.hierarchy-caret'))
                .map(x => <HTMLElement>x.target)
                .subscribe(function(x) {
                    $(x).toggleClass('fa-caret-right fa-caret-down');
                    x.parentElement.querySelector('.hierarchy-child').classList.toggle('hierarchy-active');
                    if($(x).hasClass('fa-caret-right')){
                        // change selection to the node being collapsed, if that node's child is the selected item
                        const selectedItem = $(x).parent().find('.hierarchy-selected')[0];
                        if(selectedItem !== undefined){
                            $(selectedItem).removeClass('hierarchy-selected');
                            const parentElement = $(x).parent().find('span')[0];
                            const parentLookupItem = <LookupItem>($(parentElement).data('lookupitem'));
                            $(parentElement).addClass(selectionClass);
                            this.scrollItemIntoView($(parentElement), $popup);
                            this.setText(parentLookupItem.displayValue);
                            selection.next(parentLookupItem);
                        }
                        x.parentElement.querySelector('ul').querySelectorAll('span.available').forEach(span => $(span).toggleClass('available unavailable'));
                    }else{
                        x.parentElement.querySelector('ul').childNodes.forEach(li => $(li.querySelector('span')).toggleClass('available unavailable'));
                        this.checkActiveChildren(x);
                    }
                    this.tempItemsList = this.filterHierarchyItems(filteredItems, document);
                }.bind(this)),

            rxui.mousedownleft($popup.find('i span,li span'))
                .map(x => $(x.target))
                .map(x => <LookupItem>(x.data('lookupitem')))
                .race(rxui.filteredKeys(focusedArea, [$.ui.keyCode.ENTER]).withLatestFrom(selection.asObservable(), (x, y) => y))
                .subscribe(x =>  {

                    this.select(x, true); }),

            rxui.scrollOutside($popup).first().subscribe(() => this.closeList()),

            selection

        ));
    }

    private scrollItemIntoView($item: JQuery, $popup: JQuery) {
        const st = $popup.children('div')[0].scrollTop;

        if ($item.position().top + $item.height() > st + $popup.height())
            $item[0].scrollIntoView(false);
        else if (st > $item.position().top)
            $item[0].scrollIntoView(true);
    }

    private setText(text: string) {
        this.updating = true;
        this.$input.val(text);
        this.updating = false;
    }

    private select(item: LookupItem, raiseSelected: boolean) {
        if (item === null || !item.active) {
            return;
        }

        this.setText(item.displayValue);
        this.value.next(item);
        this.closeList();

        if (raiseSelected)
            this.triggerEvent('Selection Made');
    }

    private closeList() {
        this.listDisposable.set(null);
        this.listOpen = false;
        this.focusGroup.next(this.$input);
    }

    private getLookupItems(filter: string): Observable<LookupResult> {
        return this.eventAggregator.request(new FilteredLookupItemsRequested(this.fieldBindingSource('Content.SelectedValue'), this.fieldContext, "")).map(x => new LookupResult(filter, x));
    }

    private getLookupItemByDisplay(display: string): Observable<LookupItem> {
        return this.eventAggregator.request(new LookupItemByDisplayRequested(this.fieldBindingSource('Content.SelectedValue'), this.fieldContext, display));
    }

    private getLookupItemByKey(key: string): Observable<LookupItem> {
        return this.eventAggregator.request(new LookupItemRequested(this.fieldBindingSource('Content.SelectedValue'), this.fieldContext, key));
    }

    'content.SelectedValue'(value: string) {
        this.checkInitialValueReceived();
        this.setValues.next(value);
    }

    focus() {
        this.$input.focus();
    }

    applyEnabledState(value: boolean) {
        this.$input.prop('readonly', !value);
        this.$icon.conditional(value, x => x.show()).conditional(!value, x => x.hide());
        this.readyTrigger.next(null);
    }

    tabIndexElements() {
        return this.$input;
    }
}

registerBinding('hierarchy', Hierarchy);